import cx from "classnames";
import { Location, LocationDescriptorObject } from "history";
import { stringify } from "query-string";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { CellMeasurer, CellMeasurerCache, ListRowProps } from "react-virtualized";

import GridItem, { GridItemPlaceholder } from "@/mk2/apps/bazaar/components/GridItem";
import { ProductEntity } from "@/mk2/apps/bazaar/schemas";
import { bazaarGridUrl } from "@/mk2/apps/bazaar/urls";
import { InfiniteListLoadable } from "@/mk2/components/InfiniteListLoadable";
import { Left, LineHeader } from "@/mk2/components/LineHeader";
import { Link } from "@/mk2/components/Link";
import { TrackClick } from "@/mk2/components/TrackClick";
import { AdSlot } from "@/mk2/containers/AdSlot/AdSlot";
import { url } from "@/mk2/helpers/urls";
import { LoadingState } from "@/mk2/reducers/loadingState";
import {
    BZ2_NO_RELATED_ITEMS_FOUND,
    RELATED_BAZAAR_PRODUCTS,
} from "@/mk/autogenerated/translation";
import { dict } from "@/mk/bazaar/product/categories";
import {
    filterByCategoryAndProps,
    serializeFilterForPathname,
    serializeFilterForQuery,
} from "@/mk/bazaar/product/filter/filterUtils";

import styles from "./RelatedProducts.mscss";

const DEFAULT_ROW_HEIGHT = 397; // Based on desktop

interface OwnProps {
    className?: string;
    relatedProducts: ProductEntity[];
    product?: ProductEntity;
    hasNext: boolean;
    loadingState: LoadingState;
    isMobile: boolean;
    location: Location;

    onLoad(start: number);
}

type Props = OwnProps;

const RelatedProducts = React.memo<Props>(
    ({
        className,
        relatedProducts,
        product,
        loadingState,
        hasNext,
        isMobile,
        location,
        onLoad,
    }) => {
        let gridCategoryUrl: LocationDescriptorObject;
        if (product) {
            const categoryProperty = product.properties.find((p) => p[0] === "cat");
            const categoryName = categoryProperty[1];
            const categoryDefinition = dict()[categoryName];

            const importantProps =
                categoryDefinition !== undefined && categoryDefinition.pathnameFilters !== undefined
                    ? categoryDefinition.pathnameFilters
                    : [];

            const props = importantProps.reduce((acc, cur) => {
                const prop = product.properties.filter((p) => p[0] === cur);
                if (prop) {
                    acc[cur] = prop.map((p) => p[1]);
                }
                return acc;
            }, {});
            const catFilter = filterByCategoryAndProps(categoryName, props);
            gridCategoryUrl = {
                pathname: url(bazaarGridUrl, {
                    category: categoryDefinition && categoryDefinition.slug,
                    filters: serializeFilterForPathname(catFilter),
                }),
                search: stringify(serializeFilterForQuery(catFilter)),
            };
        }

        const [client, setClient] = useState<boolean>(false);

        const cacheRef = useRef<CellMeasurerCache>(
            new CellMeasurerCache({
                defaultHeight: DEFAULT_ROW_HEIGHT,
                fixedWidth: true,
            }),
        );
        const rowItems = isMobile ? 2 : 4;
        const realRowsCount =
            Math.floor(relatedProducts.length / rowItems) +
            (relatedProducts.length % rowItems === 0 ? 0 : 1);

        // On mobile, insert ad row after every 5 product rows (10 products)
        // Limit to maximum 2 ad slots (can be increased to 3-4 later)
        const adRowInterval = 5;
        const maxAdSlots = 2;
        const possibleAdRows = isMobile ? Math.floor(realRowsCount / adRowInterval) : 0;
        const numberOfAdRows = Math.min(possibleAdRows, maxAdSlots);
        const totalRowsCount = realRowsCount + numberOfAdRows;
        const rowCount = hasNext ? totalRowsCount + 1 : totalRowsCount;

        const onLoadCallback = useCallback(
            () => onLoad(relatedProducts.length),
            [onLoad, relatedProducts],
        );

        const isRowLoaded = useCallback(
            ({ index }) => {
                // Check if this row is an ad row - ad rows are always "loaded"
                const potentialAdNumber = Math.floor((index + 1) / (adRowInterval + 1)) - 1;
                const isAdRow =
                    isMobile &&
                    (index + 1) % (adRowInterval + 1) === 0 &&
                    index >= adRowInterval &&
                    potentialAdNumber < numberOfAdRows;
                if (isAdRow) {
                    return true;
                }

                // Calculate the actual product row index
                const adsBeforeThisRow = Math.min(
                    Math.floor((index + 1) / (adRowInterval + 1)),
                    numberOfAdRows,
                );
                const productRowIndex = index - adsBeforeThisRow;

                // Check if we have products for this product row
                return !hasNext || (productRowIndex + 1) * rowItems < relatedProducts.length;
            },
            [hasNext, rowItems, relatedProducts, isMobile, adRowInterval, numberOfAdRows],
        );

        const renderRow = useCallback(
            (listRowProps: ListRowProps) => {
                const { index, key, style, parent } = listRowProps;

                // Check if this is an ad row (on mobile only, after every 5 product rows)
                // Ad rows appear at indices: 5, 11, (but limited by maxAdSlots)
                const potentialAdNumber = Math.floor((index + 1) / (adRowInterval + 1)) - 1;
                const isAdRow =
                    isMobile &&
                    (index + 1) % (adRowInterval + 1) === 0 &&
                    index >= adRowInterval &&
                    potentialAdNumber < numberOfAdRows;

                if (isAdRow) {
                    // Determine which ad slot to show (Other 01, Other 02, etc.)
                    const adNumber = potentialAdNumber;
                    const adZone =
                        adNumber === 0
                            ? "Market Item - Other 01 - Mobile"
                            : "Market Item - Other 02 - Mobile";

                    return (
                        <CellMeasurer
                            key={key}
                            cache={cacheRef.current}
                            parent={parent}
                            columnIndex={0}
                            rowIndex={index}
                        >
                            <div
                                style={{
                                    ...style,
                                    ...(!client ? { position: undefined } : null),
                                }}
                            >
                                <AdSlot
                                    key={`ad-${adNumber}`}
                                    zone={adZone}
                                    location={location}
                                    marginTopWithAd={20}
                                    marginBottomWithAd={20}
                                />
                            </div>
                        </CellMeasurer>
                    );
                }

                // Calculate the actual product row index (accounting for ad rows before this row)
                const adsBeforeThisRow = Math.min(
                    Math.floor((index + 1) / (adRowInterval + 1)),
                    numberOfAdRows,
                );
                const productRowIndex = index - adsBeforeThisRow;

                const rowGroups = relatedProducts
                    .slice(productRowIndex * rowItems, (productRowIndex + 1) * rowItems)
                    .map((prod, i) => (
                        <TrackClick
                            key={prod.id}
                            name="related_product_click"
                            props={{
                                product_id: product?.id,
                                related_product_id: prod.id,
                                row: productRowIndex,
                                column: i,
                            }}
                        >
                            <GridItem
                                key={prod.id}
                                className={cx(
                                    styles.RelatedProducts__item,
                                    styles[`RelatedProducts__item--col${i % 4}`],
                                )}
                                showCreatedTime={false}
                                showStateBadges={false}
                                product={prod}
                            />
                        </TrackClick>
                    ));

                while (rowGroups.length < rowItems) {
                    rowGroups.push(
                        !isRowLoaded({ index }) ? (
                            <GridItemPlaceholder
                                key={`placeholder-${rowGroups.length}`}
                                className={cx(
                                    styles.RelatedProducts__item,
                                    styles[`RelatedProducts__item--col${rowGroups.length % 4}`],
                                )}
                            />
                        ) : (
                            <div
                                key={`empty-${rowGroups.length}`}
                                className={cx(
                                    styles.RelatedProducts__item,
                                    styles[`RelatedProducts__item--col${rowGroups.length % 4}`],
                                )}
                            />
                        ),
                    );
                }

                return (
                    <CellMeasurer
                        key={key}
                        cache={cacheRef.current}
                        parent={parent}
                        columnIndex={0}
                        rowIndex={index}
                    >
                        <div
                            style={{
                                ...style,
                                // Fix SSR rendering, because we are using dynamic row heights
                                ...(!client ? { position: undefined } : null),
                            }}
                        >
                            <div className={styles.RelatedProducts__row}>{rowGroups}</div>
                        </div>
                    </CellMeasurer>
                );
            },
            [
                relatedProducts,
                product,
                rowItems,
                isRowLoaded,
                isMobile,
                location,
                adRowInterval,
                numberOfAdRows,
            ],
        );

        useEffect(() => {
            setClient(true);
        }, [setClient]);

        // Clear cache when number of ad rows changes to prevent scroll issues
        useEffect(() => {
            cacheRef.current.clearAll();
        }, [numberOfAdRows]);

        return (
            <div className={cx(styles.RelatedProducts, className)}>
                <LineHeader>
                    <Left>
                        <h2 className={styles.RelatedProducts__title}>
                            {gridCategoryUrl ? (
                                <Link to={gridCategoryUrl}>{RELATED_BAZAAR_PRODUCTS}</Link>
                            ) : (
                                RELATED_BAZAAR_PRODUCTS
                            )}
                        </h2>
                    </Left>
                </LineHeader>

                <div
                    className={cx(
                        styles.RelatedProducts__items,
                        relatedProducts.length === 0 && styles["RelatedProducts__items--empty"],
                    )}
                >
                    {relatedProducts.length > 0 ? (
                        <InfiniteListLoadable
                            isRowLoaded={isRowLoaded}
                            loadMoreRows={onLoadCallback}
                            rowHeight={cacheRef.current.rowHeight}
                            rowCount={rowCount}
                            rowRenderer={renderRow}
                            estimatedRowHeight={DEFAULT_ROW_HEIGHT}
                            deferredMeasurementCache={cacheRef.current}
                            numberOfItemsOnFirstPage={2}
                        />
                    ) : (
                        BZ2_NO_RELATED_ITEMS_FOUND
                    )}
                </div>
            </div>
        );
    },
);

RelatedProducts.displayName = "RelatedProducts";

export default RelatedProducts;

interface PlaceholderOwnProps {
    className?: string;
}

export const RelatedProductsPlaceholder = React.memo<PlaceholderOwnProps>(({ className }) => (
    <div className={cx(styles.RelatedProducts, className)}>
        <LineHeader>
            <Left>
                <h2 className={styles.RelatedProducts__title}>{RELATED_BAZAAR_PRODUCTS}</h2>
            </Left>
        </LineHeader>

        <div className={cx(styles.RelatedProducts__items)}>
            {Array.from(Array(8).keys()).map((index) => (
                <GridItemPlaceholder
                    key={index}
                    className={cx(
                        styles.RelatedProducts__item,
                        styles[`RelatedProducts__item--col${index % 4}`],
                    )}
                />
            ))}
        </div>
    </div>
));
